import React, { useEffect, useState, useRef } from "react";
import { Grid, TextField, Autocomplete, FormControl, InputLabel, Select, MenuItem, Alert, Snackbar, InputAdornment, IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "../../auth/Auth";
import { usePermission } from "../../auth/usePermission";
import { useNavigate } from 'react-router-dom';
import Box from "@mui/material/Box";
import colors from "assets/theme/base/colors";
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { useCurrency } from '../../context/CurrencyContext';
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import AddressAutocompleteInput from "../../layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput";

const ProductForm = ({ handleCloseModal, onProductCreated }) => {
  const { user, axiosInstance } = useAuth();
  const { isSupportUser } = usePermission('products');
  const { currency, formatCurrency, getCurrencySymbol, availableCurrencies } = useCurrency();
  const navigate = useNavigate();
  
  const [newProduct, setNewProduct] = useState({
    name: '',
    sku: '',
    hsn_sac_code: '',
    category: '',
    brand: '',
    manufacturer: '',
    price: '',
    tax_rate: '',
    discount_percentage: '',
    lead_time: '',
    description: '',
    quantity: '',
    location: '',
    product_type: 'Physical',
    currency: currency, // Add this line - default from context
  });
  
  const [suggestions, setSuggestions] = useState({
    locations: [],
    categories: [],
    brands: [],
    manufacturers: [],
    names: [],
  });
    const [customFields, setCustomFields] = useState([]);
  const customFieldsRef = useRef(null);
  const [formErrors, setFormErrors] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  const [inventoryError, setInventoryError] = useState(false);
  
  // Update the currency symbol to use the selected currency from newProduct
  const currencySymbol = getCurrencySymbol(newProduct.currency || currency);

  useEffect(() => {
    const extractUniqueSuggestions = async () => {
      try {
        console.log("Starting to fetch product and inventory suggestions...");
        
        // Fetch products first
        let productsResponse;
        try {
          console.log("Fetching products data...");
          productsResponse = await axiosInstance.get("/products/");
          console.log("Products data fetched successfully:", productsResponse.data);
        } catch (productError) {
          console.error("Failed to fetch products:", {
            status: productError.response?.status,
            message: productError.message,
            response: productError.response?.data
          });
          productsResponse = { data: [] };
        }

        // Don't try to fetch inventory details since they're now part of the product model
        const extractUnique = (arr, field) => {
          const uniqueValues = [...new Set(arr.map((item) => item[field]).filter((value) => value && value.trim() !== ""))];
          console.log(`Extracted unique ${field}s:`, uniqueValues);
          return uniqueValues.sort((a, b) => a.localeCompare(b));
        };

        const newSuggestions = {
          locations: extractUnique(productsResponse.data, "location"),
          categories: extractUnique(productsResponse.data, "category"),
          brands: extractUnique(productsResponse.data, "brand"),
          manufacturers: extractUnique(productsResponse.data, "manufacturer"),
          names: extractUnique(productsResponse.data, "name"),
        };

        console.log("Final processed suggestions:", newSuggestions);
        setSuggestions(newSuggestions);

      } catch (error) {
        console.error("Critical error in extractUniqueSuggestions:", {
          message: error.message,
          stack: error.stack,
          response: error.response?.data
        });
        setSuggestions(prev => ({
          ...prev,
          locations: [],
          categories: [],
          brands: [],
          manufacturers: [],
          names: []
        }));
      }
    };

    extractUniqueSuggestions();
  }, []);

  // Add useEffect to fetch custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get('/custom-fields/products');
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Special handler for price field to handle currency formatting
  const handlePriceChange = (event) => {
    const rawValue = event.target.value;
    
    // Remove any non-numeric characters except decimal point
    const numericValue = rawValue.replace(/[^0-9.]/g, '');
    
    // Ensure it's a valid number or empty
    if (numericValue === '' || !isNaN(parseFloat(numericValue))) {
      setNewProduct(prev => ({
        ...prev,
        price: numericValue
      }));
      if (formErrors.price) {
        setFormErrors((prev) => ({ ...prev, price: '' }));
      }
    }
  };

  const handleProductTypeChange = (event) => {
    const selectedType = event.target.value;
    setNewProduct(prev => ({
      ...prev,
      product_type: selectedType,
      quantity: (selectedType === 'Service' || selectedType === 'Digital') ? '' : prev.quantity
    }));
  };


  const handleSaveProduct = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create products.',
        severity: 'error',
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Validate custom fields
      const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;

      if (!isCustomFieldsValid) {
        setIsSubmitting(false);
        return;
      }

      const errors = {};
      const unitPrice = parseFloat(newProduct.price);
      const taxRate = newProduct.tax_rate === '' ? null : parseFloat(newProduct.tax_rate);
      const quantityValue = newProduct.quantity === '' ? null : parseInt(newProduct.quantity, 10);

      if (newProduct.price === '' || Number.isNaN(unitPrice) || unitPrice < 0) {
        errors.price = 'Unit price cannot be negative';
      }
      if (taxRate === null || Number.isNaN(taxRate)) {
        errors.tax_rate = 'Tax is required';
      } else if (taxRate < 0) {
        errors.tax_rate = 'Tax cannot be negative';
      } else if (taxRate > 100) {
        errors.tax_rate = 'Tax cannot exceed 100';
      }
      if (newProduct.product_type === 'Physical') {
        if (quantityValue === null || Number.isNaN(quantityValue)) {
          errors.quantity = 'Quantity is required';
        } else if (quantityValue < 0) {
          errors.quantity = 'Quantity cannot be negative';
        }
      }

      if (Object.keys(errors).length) {
        setFormErrors(errors);
        setSnackbar({
          open: true,
          message: Object.values(errors)[0],
          severity: 'error',
        });
        setIsSubmitting(false);
        return;
      }
      setFormErrors({});

      const finalUnitPrice = unitPrice + (unitPrice * ((taxRate || 0) / 100));

      const productData = {
        ...newProduct,
        quantity: (newProduct.product_type === 'Service' || newProduct.product_type === 'Digital') ? null : quantityValue,
        lead_time: newProduct.lead_time === '' ? null : parseInt(newProduct.lead_time, 10),
        price: unitPrice,
        tax_rate: taxRate,
        discount_percentage: newProduct.discount_percentage === '' ? null : parseFloat(newProduct.discount_percentage),
        final_unit_price: finalUnitPrice,
        created_by: user?.id,
        updated_by: user?.id,
        created_by_name: user?.username || "N/A",
        updated_by_name: user?.username || "N/A",
        currency: newProduct.currency,
      };
      delete productData.created_at;
      delete productData.updated_at;
      console.log("Product Data to be saved:", productData);

      const productResponse = await axiosInstance.post("/products/", productData);

      handleCloseModal();
      if (onProductCreated) {
        onProductCreated();
      }
      navigate(`/products/${productResponse.data.id}`);
    } catch (error) {
      console.error("Error saving product:", error);
      const detail = error.response?.data?.detail;
      let message = "Failed to save product. Please try again.";
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        message = detail.map((d) => d.msg).join('; ');
      }
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFieldUpdate = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFieldError = (field, error) => {
    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setFormErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      maxHeight: '90vh',
    }}>
      {/* Header */}
      <Box className="pearl-popup-header"
        sx={{
          backgroundColor: colors.popupHeader.background,
          px: 4,
          py: 3,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <MDTypography
          variant="h6"
          component="h2"
          sx={{ color: '#FFFFFF' }}
        >
          Add New Product
        </MDTypography>
        <IconButton
          aria-label="Close"
          onClick={handleCloseModal}
          sx={{ color: '#FFFFFF', ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Add warning for inventory error */}
      {inventoryError && (
        <Alert 
          severity="warning" 
          sx={{ mx: 4, mt: 2 }}
        >
          Unable to load inventory locations. You can still manually enter a location.
        </Alert>
      )}

      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 4,
          '&::-webkit-scrollbar': {
            width: '0.4em'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555'
          }
        }}
      >
        <form onSubmit={handleSaveProduct}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={suggestions.names}
                value={newProduct.name}
                onChange={(event, newValue) => handleInputChange({ target: { name: "name", value: newValue } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Name"
                    name="name"
                    onChange={handleInputChange}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "45px",
                      },
                    }}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SKU"
                name="sku"
                value={newProduct.sku}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HSN Code"
                name="hsn_sac_code"
                value={newProduct.hsn_sac_code}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={suggestions.categories}
                value={newProduct.category}
                onChange={(event, newValue) => handleInputChange({ target: { name: "category", value: newValue } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    name="category"
                    onChange={handleInputChange}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "45px",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={suggestions.brands}
                value={newProduct.brand}
                onChange={(event, newValue) => handleInputChange({ target: { name: "brand", value: newValue } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand"
                    name="brand"
                    onChange={handleInputChange}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "45px",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={suggestions.manufacturers}
                value={newProduct.manufacturer}
                onChange={(event, newValue) => handleInputChange({ target: { name: "manufacturer", value: newValue } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Manufacturer"
                    name="manufacturer"
                    onChange={handleInputChange}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "45px",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            {newProduct.product_type === 'Physical' && (
                <Grid item xs={12} sm={6}>
                  <Box
                      sx={{
                        '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
                          height: '45px', // matches the Name/Category/Brand fields in this form
                          alignItems: 'center',
                        },
                      }}
                  >
                    <AddressAutocompleteInput
                        label="Location"
                        placeholder="Enter location"
                        value={newProduct.location || ''}
                        onChange={(nextLocation) => handleFieldUpdate('location', nextLocation)}
                    />
                  </Box>
                </Grid>
            )}
           <Grid item xs={12} sm={6}>
              {/* Updated Unit Price field with currency formatting */}
              <TextField
                fullWidth
                label="Unit Price"
                name="price"
                type="text"
                value={newProduct.price}
                onChange={handlePriceChange}
                required
                error={Boolean(formErrors.price)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getCurrencySymbol(newProduct.currency || currency)}
                    </InputAdornment>
                  ),
                }}
                // Display the formatted value as helper text
                helperText={
                  formErrors.price ||
                  (newProduct.price ? 
                  `${formatCurrency(newProduct.price, newProduct.currency || currency)}` : 
                  `Enter price in ${newProduct.currency || currency}`)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={availableCurrencies}
                value={availableCurrencies.find(curr => curr.value === newProduct.currency) || null}
                onChange={(event, newValue) => {
                  handleInputChange({ 
                    target: { 
                      name: "currency", 
                      value: newValue ? newValue.value : currency 
                    } 
                  });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Currency"
                    name="currency"
                    required
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "45px",
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax%"
                name="tax_rate"
                type="number"
                value={newProduct.tax_rate}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange({
                    target: { name: "tax_rate", value },
                  });
                }}
                required
                error={Boolean(formErrors.tax_rate)}
                helperText={formErrors.tax_rate || '0 or greater (max 100)'}
                inputProps={{ min: 0, max: 100, step: 'any' }}
              />
            </Grid>
            {newProduct.product_type === 'Physical' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lead Time (in days)"
                  name="lead_time"
                  type="number"
                  value={newProduct.lead_time}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="product-type-label">Product Type</InputLabel>
                <Select
                  labelId="product-type-label"
                  value={newProduct.product_type}
                  onChange={handleProductTypeChange}
                  label="Product Type"

                >
                  <MenuItem value="Physical">Physical</MenuItem>
                  <MenuItem value="Service">Service</MenuItem>
                  <MenuItem value="Digital">Digital</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {newProduct.product_type === 'Physical' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  name="quantity"
                  type="number"
                  value={newProduct.quantity}
                  onChange={handleInputChange}
                  required
                  error={Boolean(formErrors.quantity)}
                  helperText={formErrors.quantity || '0 or greater'}
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={newProduct.description}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
            {newProduct.product_type === 'Physical' && (
              <>
                <Grid item xs={12}>
                  <CustomFieldsSection
                    ref={customFieldsRef}
                    customFields={customFields}
                    entityType="products"
                    onFieldUpdate={(field, value) => {
                      setNewProduct(prev => ({
                        ...prev,
                        [field]: value
                      }));
                    }}
                  />
                </Grid>
                {customFields && customFields.length > 0 && (
                  <Grid item xs={12}>
                    <MDBox mt={2}>
                      <MDTypography variant="h6" gutterBottom>
                        Custom Fields
                      </MDTypography>
                      <CustomFieldsSection
                        ref={customFieldsRef}
                        objectType="products"
                        objectData={newProduct}
                        isEditing={true}
                        onFieldUpdate={handleFieldUpdate}
                        onError={handleFieldError}
                        formErrors={formErrors}
                      />
                    </MDBox>
                  </Grid>
                )}
              </>
            )}
          </Grid>

          {/* Footer Actions */}
          <MDBox
            sx={{
              borderTop: '1px solid #eee',
              pt: 3,
              mt: 4,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2
            }}
          >
            <MDButton
              onClick={handleCloseModal}
              variant="outlined"
              color="secondary"
            >
              Cancel
            </MDButton>
            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                "Save Product"
              )}
            </MDButton>
          </MDBox>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </form>
      </Box>
    </Box>
  );
};

export default ProductForm;